import { useState, useEffect } from 'react'
import { TextField, useGetIdentity, useGetOne, useRecordContext, useRefresh, useTheme, useRedirect, useListContext } from 'react-admin'
import { Chip, Badge, IconButton, Tooltip, Box } from '@mui/material'
import { useTheme as useMuiTheme } from '@mui/material/styles'

import { getRole } from '../common/roles'
import { safeUUID } from '../common/isUUID'
import { supabaseClient } from '../supabase'
import customColor from '../common/customColor'
import CustomCopyButton from '../common/CustomCopyField'
import { getItemFromLocalStorage } from '../common/LocalStorage'
import PubeurAssignation from '../leads/bulkComponents/PubeurAssignation'

import LockIcon from '@mui/icons-material/Lock'
import PersonIcon from '@mui/icons-material/Person'
import CircleIcon from '@mui/icons-material/Circle'
import CancelIcon from '@mui/icons-material/Cancel'
import PendingIcon from '@mui/icons-material/Pending'
import StarRateIcon from '@mui/icons-material/StarRate'
import VisibilityIcon from '@mui/icons-material/Visibility'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom'
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'

const customStyle = {
  padding: '5px',
  borderRadius: '5px',
  minWidth: '100px',
  fontSize: 'inherit'
}

export const TruncatedTextField = ({ source, maxLength = 25 }) => {
  const record = useRecordContext()
  const redirect = useRedirect()
  const [theme] = useTheme()
  const {textColor} = customColor(record, theme)

  if ( ! record ) return

  const style = {
    ...customStyle,
    color: textColor,
    backgroundColor: 'transparent',
  }

  const text = record[source]
  let truncatedText = text?.length > maxLength ? `${text?.substring(0, maxLength)}...` : text ?? null

  if ( source === 'last_comment' && truncatedText ) {
    truncatedText = truncatedText.replace('*TACHE*', '')
  }

  const handleClickComment = (e) => {
    e.stopPropagation()
    redirect(`/leads/${record.id}/comments`)
  }

  if ( !record[source] ) return null

  return source === 'last_comment'
    ? <Tooltip title={text} placement="top-start" onClick={(e) => handleClickComment(e)}>
        <span style={style}>{ truncatedText }</span>
      </Tooltip>
    : <span style={style}>{ truncatedText }</span>
}

export const StatusChip = ({ source }) => {
  const record = useRecordContext()
  const muiTheme = useMuiTheme()

  if ( !record ) return null

  const name = record[source]
  if ( !name ) return null

  const lower = name.toLowerCase()
  const statusPalette = muiTheme.palette.status

  let colors
  if ( lower.includes('@') ) {
    colors = statusPalette.email
  } else {
    colors = statusPalette.inscrit
  }
  // if ( lower.includes('recontacter') ) {
  //   colors = statusPalette.recontacter
  // } else if ( lower.includes('en cours') || lower.includes('encours') ) {
  //   colors = statusPalette.encours
  // } else if ( lower.includes('intéressé') || lower.includes('interesse') ) {
  //   colors = statusPalette.encours
  // } else if ( lower.includes('inscrit') || lower.includes('candidat') ) {
  //   colors = statusPalette.inscrit
  // } else if ( lower.includes('refus') ) {
  //   colors = statusPalette.refuse
  // } else if ( lower.includes('rgpd') || lower.includes('verrouill') ) {
  //   colors = statusPalette.rgpd
  // } else {
  //   colors = { bg: '#f5f5f5', fg: '#555', border: '#ccc' }
  // }

  const isRgpd = lower.includes('rgpd') || lower.includes('verrouill')
  const fullLabel = isRgpd ? `🔒 ${name}` : name
  const label = fullLabel.length > 15 ? `${fullLabel.slice(0, 15)}...` : fullLabel

  return colors && (
    <span style={{
      padding: '3px 9px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: 600,
      border: `1px solid ${colors?.border}`,
      backgroundColor: colors?.bg,
      color: colors?.fg,
      whiteSpace: 'nowrap',
    }}>
      { label }
    </span>
  )
}

export const PubeurTextField = () => {
  const record = useRecordContext()
  const refresh = useRefresh()
  const [theme] = useTheme()
  const isDarkTheme = theme === 'dark'

  const { data: pubeurProfile } = useGetOne(
    'profiles',
    { id: record?.assigned_pubeur },
    { enabled: !!record?.assigned_pubeur }
  )
  const pubeurName = pubeurProfile?.full_name

  const [openAssignation, setOpenAssignation] = useState(false)

  if ( !record ) return null

  const { textColor, bgColor } = customColor(record, theme)

  const style = {
    fontSize: '8pt',
    color: record.momentary_assignation?.isMomentary
      ? '#fefefe'
      : ( bgColor !== '#A82825' && bgColor !== '#D5433F' )
        ? isDarkTheme ? '#fefefe' : '#232323'
        : textColor,
    backgroundColor: record.momentary_assignation?.isMomentary
      ? isDarkTheme ? '#177b9c' : '#1c94bc'
      : ( bgColor !== '#A82825' && bgColor !== '#D5433F' )
        ? isDarkTheme ? '#aea8a4' : '#f9ceae'
        : bgColor,
  }

  const removemomentary_assignation = async (e) => {
    e.stopPropagation()

    const { error: errorUpdatingAssignationLog } = await supabaseClient
      .from('assignation_logs')
      .update({ duration: null })
      .eq('id', record.momentary_assignation.assignationLogId)

    if (errorUpdatingAssignationLog) {
      console.error('Error removing momentary assignation:', errorUpdatingAssignationLog)
      return
    }

    const { error: errorUpdatingLead } = await supabaseClient
      .from('leads')
      .update({ assigned_pubeur: record.momentary_assignation.originalPubeur })
      .eq('id', safeUUID(record.id))

    if (errorUpdatingLead) {
      console.error('Error removing momentary assignation:', errorUpdatingLead)
    }

    refresh()
  }

  const addPubeurToLead = (e) => {
    e.stopPropagation()
    setOpenAssignation(true)
  }

  const handleCloseAssignation = (e) => {
    if (e) {
      e.stopPropagation()
    }

    setOpenAssignation(false)
  }

  return (
    <>
      {pubeurName ? (
        <Chip
          sx={style}
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
              {record.momentary_assignation?.isMomentary && (
                <HistoryToggleOffIcon style={{ color: '#fefefe' }} />
              )}

              <span>{pubeurName}</span>

              {record.momentary_assignation?.isMomentary && (
                <CancelIcon
                  onClick={(e) => removemomentary_assignation(e)}
                />
              )}
            </div>
          }
        />
      ) : (
        <Chip
          size="small"
          label="Assigner..."
          onClick={addPubeurToLead}
          sx={{ color: '#999', backgroundColor: isDarkTheme ? '#333' : '#f0f0f0', cursor: 'pointer', fontSize: '8pt' }}
        />
      )}

      {openAssignation && (
        <div onClick={(e) => e.stopPropagation()}>
          <PubeurAssignation
            defaultOpen={true}
            onCloseCallback={handleCloseAssignation}
            leadIds={[record.id]}
            initialPubeurId={record.assigned_pubeur}
            initialPubeurName={pubeurName}
          />
        </div>
      )}
    </>
  )
}

export const TasksTextField = () => {
  const info = useRecordContext()
  const redirect = useRedirect()

  const tasks = info?.tasks_info
  
  if ( ! tasks || tasks.length === 0 || info?.previous ) return null

  let countNotStarted = 0
  let countInProgress = 0
  let countIsWaiting = 0

  tasks.forEach(task => {
    if ( task?.progress === 100 ) return

    if ( task?.progress === 0 || task?.progress === null ) {
      countNotStarted++
    } else if ( task?.is_waiting !== null && task?.callback ) {
      const isWaiting = task?.is_waiting

      if ( isWaiting ) {
        countIsWaiting++
      } else {
        countInProgress++
      }
    } else if ( task?.progress > 0 && task?.is_waiting !== true ) {
      countInProgress++
    }
  })

  const goToTasks = (e) => {
    e.stopPropagation()
    redirect('/leads/' + info?.id + '/tasks')
  }

  return (
    <>
      {countNotStarted > 0 && (
        <Tooltip title={`${countNotStarted} tâche${countNotStarted > 1 ? 's' : ''} non commencée${countNotStarted > 1 ? 's' : ''}`}>
          <IconButton size="small" onClick={goToTasks}>
            <Badge badgeContent={countNotStarted > 1 ? countNotStarted : 0} color="primary" max={99}>
              <AssignmentTurnedInIcon sx={{ color: 'primary.main', fontSize: '1.1rem' }} />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      {countInProgress > 0 && (
        <Tooltip title={`${countInProgress} tâche${countInProgress > 1 ? 's' : ''} en cours`}>
          <IconButton size="small" onClick={goToTasks}>
            <Badge badgeContent={countInProgress > 1 ? countInProgress : 0} color="warning" max={99}>
              <AssignmentTurnedInIcon color="warning" sx={{ fontSize: '1.1rem' }} />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      {countIsWaiting > 0 && (
        <Tooltip title={`${countIsWaiting} tâche${countIsWaiting > 1 ? 's' : ''} en attente`}>
          <IconButton size="small" disabled>
            <Badge badgeContent={countIsWaiting > 1 ? countIsWaiting : 0} color="warning" max={99}>
              <PendingIcon sx={{ color: '#FFCF9D', fontSize: '1.1rem' }} />
            </Badge>
          </IconButton>
        </Tooltip>
      )}
    </>
  )
}

export const SourceTextField = () => {
  const info = useRecordContext()
  const { filterValues } = useListContext()
  const muiTheme = useMuiTheme()
  const associatedLeads = filterValues['displayLinkedLeads']
  const baseSx = { fontSize: '1rem', zIndex: 10 }

  if ( info.source_id ) {
    info.lead_source_id = info.source_id
  }

  let sourceIcon
  if ( info.stop_rgpd ) {
    const rgpd = muiTheme.palette.status.rgpd
    sourceIcon = (
      <Tooltip title="Verrouillé RGPD">
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: '50%',
          backgroundColor: rgpd.bg,
          border: `1px solid ${rgpd.border}`,
          zIndex: 10
        }}>
          <LockIcon sx={{ fontSize: '1rem', color: rgpd.fg }} />
        </Box>
      </Tooltip>
    )
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_WEB ) {
    sourceIcon = <Tooltip title="Contact web"><CircleIcon sx={{ color: '#dfed72', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_MOBILE ) {
    sourceIcon = <Tooltip title="Contact mobile"><CircleIcon sx={{ color: '#19e519', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_NOMAD ) {
    sourceIcon = <Tooltip title="NOMAD"><CircleIcon sx={{ color: '#efa87c', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_BROCHURES ) {
    sourceIcon = <Tooltip title="Demande de brochure"><CircleIcon sx={{ color: '#c29cd8', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_CSV_IMPORT ) {
    sourceIcon = <Tooltip title="Import CSV"><CircleIcon sx={{ color: '#51edc7', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_DIPLOMEO ) {
    sourceIcon = <Tooltip title="Contact Diplomeo"><CircleIcon sx={{ color: '#344CBE', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_HODEA ) {
    sourceIcon = <Tooltip title="Contact Hodea"><CircleIcon sx={{ color: '#555c7d', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_Q_PARCOURSUP ) {
    sourceIcon = <Tooltip title="Question Parcoursup"><CircleIcon sx={{ color: '#d861cc', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_DEMANDE_RAPPEL ) {
    sourceIcon = <Tooltip title="Demande de rappel"><CircleIcon sx={{ color: '#e33460', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_HIPPOCAST ) {
    sourceIcon = <Tooltip title="Import Hippocast"><CircleIcon sx={{ color: '#c5efac', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_STUDYRAMA ) {
    sourceIcon = <Tooltip title="Import Studyrama"><CircleIcon sx={{ color: '#004874', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_THOTIS ) {
    sourceIcon = <Tooltip title="Import Thotis"><CircleIcon sx={{ color: '#d2aeb7', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_JPO ) {
    sourceIcon = <Tooltip title="Journée Portes Ouvertes"><CircleIcon sx={{ color: '#af6aa2ff', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_JI ) {
    sourceIcon = <Tooltip title="Journée d'Immersion"><CircleIcon sx={{ color: '#52c3e6ff', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_INTERVENTION ) {
    sourceIcon = <Tooltip title="Intervention"><CircleIcon sx={{ color: '#f889e4ff', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_SALON ) {
    sourceIcon = <Tooltip title="Salon"><CircleIcon sx={{ color: '#b6219bff', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_FORUM ) {
    sourceIcon = <Tooltip title="Forum"><CircleIcon sx={{ color: '#50394bff', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_MISSIONLYCEE ) {
    sourceIcon = <Tooltip title="Mission Lycée"><CircleIcon sx={{ color: '#d0b73cff', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_EDN_HIPPOCRATE ) {
    sourceIcon = <Tooltip title="EDN Hippocrate"><CircleIcon sx={{ color: '#a9ca35', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_MEP ) {
    sourceIcon = <Tooltip title="Mon Espace Prépa"><CircleIcon sx={{ color: '#20df7d', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_RDV ) {
    sourceIcon = <Tooltip title="Demande de RDV"><CircleIcon sx={{ color: '#e5dec6ff', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_LETUDIANT ) {
    sourceIcon = <Tooltip title="Létudiant"><CircleIcon sx={{ color: 'rgb(240, 210, 238)', ...baseSx }} /></Tooltip>
  } else if ( info.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_GALIEN ) {
    sourceIcon = <Tooltip title="Galien"><CircleIcon sx={{ color: 'rgb(52, 60, 120)', ...baseSx }} /></Tooltip>
  } else {
    sourceIcon = <Tooltip title="Origine inconnue"><CircleIcon sx={{ color: '#eee', ...baseSx }} /></Tooltip>
  }

  return associatedLeads
    ? (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: 40
      }}>
        { sourceIcon }
        { info.parent ? <FamilyRestroomIcon sx={{ fontSize: '10pt', ml: 2 }} /> : <PersonIcon /> }
      </Box>
    ) : (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: 6
      }}>
        { sourceIcon }
      </Box>
    )
}

export const InfoTextField = record => {
  const info = useRecordContext()
  const [theme] = useTheme()
  const {textColor} = customColor(info, theme)

  if ( ! info ) return null

  const style = {
    ...customStyle,
    color: textColor,
    backgroundColor: 'transparent',
    width: '10%'
  }

  return info[record.source]
    ? <TextField source={record.source} sx={style} />
    : null
}

export const YellowTextField = record => {
  const info = useRecordContext()

  const style = {
    ...customStyle,
    color: '#111',
    backgroundColor: '#f9dfbb',
  }

  return info[record.source] ? <TextField source={record.source} style={style} /> : null
}

export const TextFieldCopy = ({ source, truncated = false, maxLength = 15, style = {} }) => {
  const record = useRecordContext()

  const text = record[source]
  const truncatedText = truncated
    ? text?.length > maxLength ? `${text?.substring(0, maxLength)}...` : text ?? null
    : text

  return record && record[source] ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...style }}>
      <span>{truncatedText}</span>

      <CustomCopyButton
        content={record ? record[source] : null}
        notification="ID copié dans le presse-papier"
        iconFontSize="small"
        sx={{ marginLeft: '5px' }}
      />
    </div>
  ) : null
}

export const FavouriteTextField = () => {
  const info = useRecordContext()
  const { data: identity } = useGetIdentity()
  const currentUser = identity?.id
  const isPubeur = identity?.role?.grade < getRole('responsable')

  if (info?.stop_rgpd) return <StarOutlineIcon sx={{ color: '#ccc', pointerEvents: 'none' }} />

  if (!info?.favourited_by) return <StarOutlineIcon />

  if (info?.assigned_pubeur === null || info?.favourited_by?.length === 0) return null

  let userEntry = null
  if ( ! isPubeur ) {
    userEntry = info?.favourited_by?.find(entry => entry?.id === info?.assigned_pubeur)
  } else {
    userEntry = info?.favourited_by?.find(entry => entry?.id === currentUser)
  }

  if (!userEntry) return <StarOutlineIcon />

  return userEntry.is_fav ? <StarRateIcon /> : <StarOutlineIcon />
}

export const CustomFirstNameField = ({ source, label, ...props }) => {
  const record = useRecordContext()

  const [hasBeenViewed, setHasBeenViewed] = useState(false)

  useEffect(() => {
    const checkViewed = () => {
      if (record?.id) {
        const viewedLeadId = getItemFromLocalStorage('viewedLeads')
        setHasBeenViewed(viewedLeadId === record.id)
      }
    }

    checkViewed()

    // Listen for storage changes
    window.addEventListener('storage', checkViewed)
    // Listen for custom event when localStorage is updated in the same tab
    window.addEventListener('viewedLeadUpdated', checkViewed)

    return () => {
      window.removeEventListener('storage', checkViewed)
      window.removeEventListener('viewedLeadUpdated', checkViewed)
    }
  }, [record?.id])

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {hasBeenViewed && (
        <VisibilityIcon sx={{ fontSize: '1rem', color: '#a1ca87' }} />
      )}
      
      <InfoTextField source={source} label={label} {...props} />
    </Box>
  )
}